import { createAction, createAsyncAction } from "typesafe-actions";
import {
  NicheFilesFilter,
  PaginatedResponse,
  ResponseError,
  WithCallback,
  WithOptionalLoader,
} from "@shared/interfaces";
import {
  NicheFilesCountyPriceResult,
  NicheFilesCreateSubscriptionPayload,
  NicheFilesFetchCountyPricePayload,
  NicheFilesGetSubscriptionPayload,
  NicheFilesError,
  NicheFilesCancelSubscriptionPayload,
  NicheFilesCreateCheckoutLinkPayload,
  NicheFilesGetSubscriptionsPayload,
} from "@containers/NicheFiles/interface";
import { NicheFile, County, NicheFilesSubscription, NICHE_FILE_STATUS } from "@shared/models";

import { NicheFilesActionTypes } from "./constants";

export const fetchNicheFilesCountyPrice = createAsyncAction(
  NicheFilesActionTypes.FETCH_COUNTY_PRICE,
  NicheFilesActionTypes.FETCH_COUNTY_PRICE_SUCCESS,
  NicheFilesActionTypes.FETCH_COUNTY_PRICE_FAILURE,
)<NicheFilesFetchCountyPricePayload, NicheFilesCountyPriceResult[], ResponseError>();

export const getNicheFilesSubscription = createAsyncAction(
  NicheFilesActionTypes.GET_SUBSCRIPTION,
  NicheFilesActionTypes.GET_SUBSCRIPTION_SUCCESS,
  NicheFilesActionTypes.GET_SUBSCRIPTION_FAILURE,
)<NicheFilesGetSubscriptionPayload, NicheFilesSubscription | null, ResponseError>();

export const getNicheFilesSubscriptions = createAsyncAction(
  NicheFilesActionTypes.GET_SUBSCRIPTIONS,
  NicheFilesActionTypes.GET_SUBSCRIPTIONS_SUCCESS,
  NicheFilesActionTypes.GET_SUBSCRIPTIONS_FAILURE,
)<WithCallback<NicheFilesGetSubscriptionsPayload, NicheFilesSubscription[]>, NicheFilesSubscription[], ResponseError>();

export const createNicheFilesSubscription = createAsyncAction(
  NicheFilesActionTypes.CREATE_SUBSCRIPTION,
  NicheFilesActionTypes.CREATE_SUBSCRIPTION_SUCCESS,
  NicheFilesActionTypes.CREATE_SUBSCRIPTION_FAILURE,
)<
  {
    body: NicheFilesCreateSubscriptionPayload;
    callback: () => void;
    onFailed: () => void;
  },
  NicheFilesSubscription,
  ResponseError
>();

export const cancelNicheFilesSubscription = createAsyncAction(
  NicheFilesActionTypes.CANCEL_SUBSCRIPTION,
  NicheFilesActionTypes.CANCEL_SUBSCRIPTION_SUCCESS,
  NicheFilesActionTypes.CANCEL_SUBSCRIPTION_FAILURE,
)<NicheFilesCancelSubscriptionPayload, NicheFilesSubscription, ResponseError>();

export const renewNicheFilesSubscription = createAsyncAction(
  NicheFilesActionTypes.RENEW_SUBSCRIPTION,
  NicheFilesActionTypes.RENEW_SUBSCRIPTION_SUCCESS,
  NicheFilesActionTypes.RENEW_SUBSCRIPTION_FAILURE,
)<NicheFilesCancelSubscriptionPayload, NicheFilesSubscription, ResponseError>();

export const createNicheFilesCheckoutLink = createAction(
  NicheFilesActionTypes.CREATE_CHECKOUT_LINK,
)<NicheFilesCreateCheckoutLinkPayload>();

export const addState = createAction(NicheFilesActionTypes.ADD_STATE)<number>();
export const removeState = createAction(NicheFilesActionTypes.REMOVE_STATE)<{
  stateId: number;
  counties: County[] | null;
}>();
export const addCounty = createAction(NicheFilesActionTypes.ADD_COUNTY)<number>();
export const removeCounty = createAction(NicheFilesActionTypes.REMOVE_COUNTY)<number>();

export const getNicheFiles = createAsyncAction(
  NicheFilesActionTypes.GET_NICHE_FILES,
  NicheFilesActionTypes.GET_NICHE_FILES_SUCCESS,
  NicheFilesActionTypes.GET_NICHE_FILES_FAILURE,
)<NicheFilesFilter, PaginatedResponse<NicheFile>, ResponseError>();

export const getNicheFile = createAsyncAction(
  NicheFilesActionTypes.GET_NICHE_FILE,
  NicheFilesActionTypes.GET_NICHE_FILE_SUCCESS,
  NicheFilesActionTypes.GET_NICHE_FILE_FAILURE,
)<WithOptionalLoader<{ id: number; callback?: () => void }>, NicheFile, ResponseError>();

export const deleteNicheFile = createAsyncAction(
  NicheFilesActionTypes.DELETE_NICHE_FILE,
  NicheFilesActionTypes.DELETE_NICHE_FILE_SUCCESS,
  NicheFilesActionTypes.DELETE_NICHE_FILE_FAILURE,
)<WithOptionalLoader<{ id: number }>, { id: number }, ResponseError>();

export const updateNicheFileStatus = createAsyncAction(
  NicheFilesActionTypes.UPDATE_NICHE_FILE_STATUS,
  NicheFilesActionTypes.UPDATE_NICHE_FILE_STATUS_SUCCESS,
  NicheFilesActionTypes.UPDATE_NICHE_FILE_STATUS_FAILURE,
)<WithOptionalLoader<{ id: number; status: NICHE_FILE_STATUS }>, NicheFile, ResponseError>();

export const setFilter = createAction(NicheFilesActionTypes.SET_FILTER)<NicheFilesFilter | null>();
export const clearNicheFiles = createAction(NicheFilesActionTypes.CLEAR_NICHE_FILES)();
export const clearNicheFile = createAction(NicheFilesActionTypes.CLEAR_NICHE_FILE)();
export const downloadNicheFile = createAction(NicheFilesActionTypes.DOWNLOAD_NICHE_FILE)<{
  id: number;
  callback?: () => void;
}>();

export const clearErrors = createAction(NicheFilesActionTypes.CLEAR_ERRORS)<NicheFilesError[] | void>();
